import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Card } from 'primereact/card';
import Menu from '../../../components/Menu';
import api from '../../../services/api';
import Loading from '../../../components/Loading';

const DEFAULT_PERCENT_LIMIT = 91;
const MIN_PERCENT_LIMIT = 80;
const MAX_PERCENT_LIMIT = 99;
const DEFAULT_GROUP_PERCENT = 90;
const MINUTES_PER_DAY = 1440;

const clampPercentLimit = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return DEFAULT_PERCENT_LIMIT;
  }

  return Math.min(MAX_PERCENT_LIMIT, Math.max(MIN_PERCENT_LIMIT, Math.trunc(parsedValue)));
};

const formatNumber = (value) => {
  return Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatPercent = (value) => {
  return `${Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
};

const getDailyProduction = (rpm, batidaPorCm, percent) => {
  const parsedRpm = Number(rpm);
  const parsedBatida = Number(batidaPorCm);

  if (!Number.isFinite(parsedRpm) || !Number.isFinite(parsedBatida) || parsedBatida <= 0) {
    return 0;
  }

  const fullProduction = ((parsedRpm / parsedBatida) / 100) * MINUTES_PER_DAY;

  return Math.floor(fullProduction * (percent / 100));
};

const buildPercentColumns = (limit) => {
  const columns = [];

  for (let percent = MAX_PERCENT_LIMIT; percent >= limit; percent -= 1) {
    columns.push(percent);
  }

  return columns;
};

const MetaProducao = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const rpmValues = useRef({});
  const [loading, setLoading] = useState(false);
  const [teares, setTeares] = useState([]);
  const [selectedPercent, setSelectedPercent] = useState(DEFAULT_GROUP_PERCENT);
  const [percentLimit, setPercentLimit] = useState(() => {
    return clampPercentLimit(localStorage.getItem('metaProducaoPercentLimit'));
  });

  const showError = useCallback((message) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Erro',
      detail: message,
      life: 4000,
    });
  }, []);

  const showSuccess = useCallback((message) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Sucesso',
      detail: message,
      life: 3000,
    });
  }, []);

  const percentColumns = useMemo(() => buildPercentColumns(percentLimit), [percentLimit]);

  const percentOptions = useMemo(() => {
    return buildPercentColumns(MIN_PERCENT_LIMIT).map((percent) => ({
      label: `${percent}%`,
      value: percent,
    }));
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);

    api.get('/relatorios-novos/meta-producao')
      .then((response) => {
        const responseData = Array.isArray(response?.data?.data) ? response.data.data : [];

        setTeares(responseData);
        showSuccess('Metas de produção carregadas.');
      })
      .catch(() => {
        showError('Não foi possível carregar as metas de produção.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [showError, showSuccess]);

  const rows = useMemo(() => {
    return teares.map((tear) => {
      const row = {
        ...tear,
        rpm: Number(tear.rpm || 0),
        batida_por_cm: Number(tear.batida_por_cm || 0),
        batida_por_cm_calculada: Math.floor(Number(tear.batida_por_cm || 0)),
      };

      percentColumns.forEach((percent) => {
        row[`percent_${percent}`] = getDailyProduction(row.rpm, row.batida_por_cm, percent);
      });

      return row;
    });
  }, [percentColumns, teares]);

  const groupedRows = useMemo(() => {
    const groups = rows.reduce((accumulator, row) => {
      const grupo = row.grupo || 'Sem grupo';
      const production = getDailyProduction(row.rpm, row.batida_por_cm, selectedPercent);

      if (!accumulator[grupo]) {
        accumulator[grupo] = {
          grupo,
          quantidade_teares: 0,
          meta_metros_dia: 0,
        };
      }

      accumulator[grupo].quantidade_teares += 1;
      accumulator[grupo].meta_metros_dia += production;

      return accumulator;
    }, {});

    const total = Object.values(groups).reduce((sum, group) => sum + group.meta_metros_dia, 0);

    return Object.values(groups)
      .map((group) => ({
        ...group,
        meta_metros_turno: group.meta_metros_dia / 2,
        proporcao_total: total > 0 ? (group.meta_metros_dia / total) * 100 : 0,
      }))
      .sort((firstGroup, secondGroup) => {
        return String(firstGroup.grupo).localeCompare(String(secondGroup.grupo), 'pt-BR', {
          numeric: true,
        });
      });
  }, [rows, selectedPercent]);

  const totalDia = useMemo(() => {
    return groupedRows.reduce((sum, group) => sum + group.meta_metros_dia, 0);
  }, [groupedRows]);

  const totalTurno = useMemo(() => {
    return groupedRows.reduce((sum, group) => sum + group.meta_metros_turno, 0);
  }, [groupedRows]);

  const totalMes = useMemo(() => totalDia * 30, [totalDia]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePercentLimitChange = (event) => {
    const nextLimit = clampPercentLimit(event.value);

    setPercentLimit(nextLimit);
    localStorage.setItem('metaProducaoPercentLimit', String(nextLimit));
  };

  const updateRpmManual = useCallback((row) => {
    const rpmValue = Object.prototype.hasOwnProperty.call(rpmValues.current, row.id)
      ? rpmValues.current[row.id]
      : row.rpm;
    const nextRpm = Number(rpmValue || 0);
    const savedRpm = Number(row.rpm_manual || 0);

    if (row.id === undefined || row.id === null) {
      showError('Tear sem identificador para salvar o RPM.');
      return;
    }

    if (!Number.isFinite(nextRpm) || nextRpm < 0) {
      showError('Informe um RPM válido.');
      return;
    }

    if (nextRpm === savedRpm) {
      delete rpmValues.current[row.id];
      return;
    }

    api.post('/atualizar-rpm-manual', {
      id: row.id,
      rpm: nextRpm,
    })
      .then((response) => {
        const savedValue = Number(response?.data?.data?.rpm ?? nextRpm);

        setTeares((currentTeares) => {
          return currentTeares.map((tear) => {
            if (tear.id !== row.id) {
              return tear;
            }

            return {
              ...tear,
              rpm: savedValue,
              rpm_manual: savedValue,
            };
          });
        });

        delete rpmValues.current[row.id];
        showSuccess('RPM atualizado com sucesso.');
      })
      .catch(() => {
        setTeares((currentTeares) => {
          return currentTeares.map((tear) => {
            if (tear.id !== row.id) {
              return tear;
            }

            return {
              ...tear,
              rpm: savedRpm,
            };
          });
        });

        delete rpmValues.current[row.id];
        showError('Não foi possível atualizar o RPM.');
      });
  }, [showError, showSuccess]);

  const handleRpmChange = useCallback((row, value) => {
    const nextRpm = Number(value || 0);

    if (!Number.isFinite(nextRpm) || nextRpm < 0) {
      showError('Informe um RPM válido.');
      return;
    }

    if (row.id !== undefined && row.id !== null) {
      rpmValues.current[row.id] = nextRpm;
    }

    setTeares((currentTeares) => {
      return currentTeares.map((tear) => {
        const hasId = row.id !== undefined && row.id !== null;
        const sameId = hasId && tear.id === row.id;
        const sameTear = !hasId && tear.tear === row.tear;

        if (!sameId && !sameTear) {
          return tear;
        }

        return {
          ...tear,
          rpm: nextRpm,
        };
      });
    });
  }, [showError]);

  const rpmEditor = useCallback((row) => {
    return (
      <InputNumber
        value={row.rpm}
        min={0}
        maxFractionDigits={2}
        onValueChange={(event) => handleRpmChange(row, event.value)}
        onBlur={() => updateRpmManual(row)}
        inputClassName="p-inputtext-sm"
      />
    );
  }, [handleRpmChange, updateRpmManual]);

  return (
    <section className="meta-producao-page">
      <Toast ref={toast} />
      <Menu />

      <Card className="p-m-1" title="Meta de Produção">
        <div className="p-d-flex p-flex-column p-md-flex-row p-md-ai-center p-jc-between p-mb-3">
          <span className="p-d-block p-mt-1">Produção diária por tear e percentual de eficiência</span>

          <div className="p-d-flex p-ai-center p-mt-3 p-md-mt-0">
            <Button
              label="Relatório de Projeção"
              icon="pi pi-file-pdf"
              className="p-button-danger p-button-outlined p-mr-2"
              onClick={() => navigate('/relatorio-projecao-producao')}
            />
            <Dropdown
              value={percentLimit}
              options={percentOptions}
              onChange={handlePercentLimitChange}
              placeholder="Colunas até"
              className="p-mr-2"
            />
          </div>
        </div>

        {loading && <Loading />}

        <DataTable
          value={rows}
          responsiveLayout="scroll"
          emptyMessage="Nenhum tear encontrado."
          loading={loading}
          className="p-mb-4"
          stripedRows
        >
          <Column field="tear" header="Tear" sortable />
          <Column field="grupo" header="Grupo" sortable />
          <Column field="artigo" header="Artigo" sortable />
          <Column field="rpm" header="RPM" sortable body={rpmEditor} />
          <Column
            field="batida_por_cm_calculada"
            header="Batida/cm"
            sortable
            body={(row) => formatNumber(row.batida_por_cm_calculada)}
          />

          {percentColumns.map((percent) => (
            <Column
              key={percent}
              field={`percent_${percent}`}
              header={`${percent}%`}
              body={(row) => formatNumber(row[`percent_${percent}`])}
              sortable
            />
          ))}
        </DataTable>

        <div className="p-d-flex p-flex-column p-md-flex-row p-md-ai-center p-jc-between p-mb-2">
          <div className="p-m-0 p-text-bold">Metas por grupo</div>

          <Dropdown
            value={selectedPercent}
            options={percentOptions}
            onChange={(event) => setSelectedPercent(event.value)}
            placeholder="Percentual"
            className="p-mt-3 p-md-mt-0"
          />
        </div>

        <DataTable
          value={groupedRows}
          responsiveLayout="scroll"
          emptyMessage="Nenhum grupo encontrado."
          className="p-mb-4"
          stripedRows
          footer={
            <div className="p-d-flex p-flex-wrap p-jc-end">
              <span className="p-mr-3">Total dia: {formatNumber(totalDia)}</span>
              <span className="p-mr-3">Total turno: {formatNumber(totalTurno)}</span>
              <span>Total mês: {formatNumber(totalMes)}</span>
            </div>
          }
        >
          <Column field="grupo" header="Grupo" sortable />
          <Column field="quantidade_teares" header="Nº de tear" sortable />
          <Column
            field="meta_metros_dia"
            header={`Meta metros/dia/${selectedPercent}%`}
            body={(row) => formatNumber(row.meta_metros_dia)}
            sortable
          />
          <Column
            field="meta_metros_turno"
            header="Meta metros/turno"
            body={(row) => formatNumber(row.meta_metros_turno)}
            sortable
          />
          <Column
            field="proporcao_total"
            header="Proporção sobre o total"
            body={(row) => formatPercent(row.proporcao_total)}
            sortable
          />
        </DataTable>
      </Card>
    </section>
  );
};

export default MetaProducao;
